import { useContext, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Add, CloudDownload, History as HistoryIcon, Refresh, UploadFile } from '@mui/icons-material';
import * as invoicesApi from '@/api/invoices';
import { hasAuthToken, toApiError } from '@/api/client';
import { listCuitProfiles } from '@/api/cuitProfiles';
import { LoadingScreen } from '@/components/LoadingScreen';
import { InvoiceFilters, EMPTY_FILTERS, type InvoiceFilterValues } from '@/components/InvoiceFilters';
import { InvoiceList } from '@/components/InvoiceList';
import { IssueInvoiceDialog } from '@/components/IssueInvoiceDialog';
import { SyncFromAfipDialog } from '@/components/SyncFromAfipDialog';
import { ImportHistoricalDialog } from '@/components/ImportHistoricalDialog';
import { useCurrentCuit } from '@/auth/CuitContext';
import { ProductContext } from '@/auth/ProductContext';
import { filterCuitProfilesForCommercialProduct } from '@/lib/cuitProfileScope';
import { formatCuit } from '@/lib/formatters';
import { AFIP_ENVIRONMENT_SHORT, CUIT_PROFILE_STATUS_LABEL } from '@/lib/labels';

export const Route = createFileRoute('/_authenticated/invoices/')({
  component: InvoicesListPage,
});

function InvoicesListPage(): JSX.Element {
  const [filters, setFilters] = useState<InvoiceFilterValues>(EMPTY_FILTERS);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(25);
  // Slice K — dialog state local. Mantenemos como useState porque es la opción más simple
  // (sin route param), siguiendo la guía de la spec.
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);
  const { currentCuitProfileId, setCurrentCuitProfile } = useCurrentCuit();
  const product = useContext(ProductContext);
  const includeDelegated = product?.activeProduct === 'Estudio';

  const invoicesQ = useQuery({
    queryKey: ['invoices', currentCuitProfileId, filters, skip, take],
    queryFn: () =>
      invoicesApi.listInvoices({
        skip,
        take,
        kind: filters.kind.length > 0 ? filters.kind : undefined,
        status: filters.status.length > 0 ? filters.status : undefined,
        cuitProfileId: currentCuitProfileId,
        from: filters.from || undefined,
        to: filters.to || undefined,
      }),
    // Auto-refresh cada 30s para que se vea "vivo" sin polling manual. La query no muestra
    // spinner durante el refetch (TanStack Query default), así que la UI no parpadea.
    refetchInterval: 30_000,
  });
  const cuitProfilesQ = useQuery({
    queryKey: ['cuit-profiles', 'invoices-empty-helper', includeDelegated],
    queryFn: () => listCuitProfiles(0, 200, { includeDelegated }),
    enabled: Boolean(currentCuitProfileId && hasAuthToken()),
    staleTime: 5 * 60_000,
  });

  const currentProfileHint = useMemo(() => {
    if (!currentCuitProfileId) return null;
    const profiles = filterCuitProfilesForCommercialProduct(
      cuitProfilesQ.data?.items ?? [],
      product?.activeCommercialProduct,
    );
    const current = profiles.find((profile) => profile.id === currentCuitProfileId);
    if (!current) return null;
    const preferredProduction = profiles.find((profile) =>
      profile.cuit === current.cuit
      && profile.id !== current.id
      && profile.environment === 'Production'
      && profile.status === 'Active'
    ) ?? null;
    return { current, preferredProduction };
  }, [currentCuitProfileId, cuitProfilesQ.data?.items, product?.activeCommercialProduct]);

  const showWrongProfileHint =
    !invoicesQ.isLoading
    && !invoicesQ.isError
    && (invoicesQ.data?.items.length ?? 0) === 0
    && Boolean(currentProfileHint?.preferredProduction);

  const handleApply = (next: InvoiceFilterValues): void => {
    setFilters(next);
    setSkip(0);
  };

  const handleClear = (): void => {
    setFilters(EMPTY_FILTERS);
    setSkip(0);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        sx={{ mb: 3, gap: 2 }}
      >
        <Box>
          <Typography variant="h4">Comprobantes</Typography>
          <Typography variant="body2" color="text.secondary">
            Emití, importá y consultá tus comprobantes desde el portal o integrá la API pública.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => invoicesQ.refetch()}
            disabled={invoicesQ.isFetching}
          >
            Actualizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={() => setSyncDialogOpen(true)}
          >
            Importar de AFIP
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoricalDialogOpen(true)}
          >
            Importar histórico
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            component={Link}
            to="/invoices/import"
          >
            Importar CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIssueDialogOpen(true)}
          >
            Emitir factura
          </Button>
        </Stack>
      </Stack>

      <InvoiceFilters
        value={filters}
        onApply={handleApply}
        onClear={handleClear}
      />

      {showWrongProfileHint && currentProfileHint?.preferredProduction ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setCurrentCuitProfile(currentProfileHint.preferredProduction!.id);
                setSkip(0);
              }}
            >
              Ver PROD activo
            </Button>
          }
        >
          Estás viendo {AFIP_ENVIRONMENT_SHORT[currentProfileHint.current.environment]} · {shortProfileStatus(currentProfileHint.current.status)}
          {' '}para el CUIT {formatCuit(currentProfileHint.current.cuit)}. Las facturas de producción están en el perfil
          {' '}{AFIP_ENVIRONMENT_SHORT[currentProfileHint.preferredProduction.environment]} · {shortProfileStatus(currentProfileHint.preferredProduction.status)} del mismo CUIT.
        </Alert>
      ) : null}

      {invoicesQ.isLoading ? (
        <LoadingScreen message="Cargando comprobantes…" />
      ) : invoicesQ.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => invoicesQ.refetch()}>
              Reintentar
            </Button>
          }
        >
          {toApiError(invoicesQ.error).message}
        </Alert>
      ) : (
        <InvoiceList
          items={invoicesQ.data?.items ?? []}
          total={invoicesQ.data?.total ?? 0}
          skip={skip}
          take={take}
          onPageChange={setSkip}
          onPageSizeChange={(newTake) => {
            setTake(newTake);
            setSkip(0);
          }}
        />
      )}

      <IssueInvoiceDialog
        open={issueDialogOpen}
        onClose={() => setIssueDialogOpen(false)}
        onDraftSaved={() => setIssueDialogOpen(false)}
      />

      <SyncFromAfipDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
      />

      <ImportHistoricalDialog
        open={historicalDialogOpen}
        onClose={() => setHistoricalDialogOpen(false)}
        onImported={() => invoicesQ.refetch()}
      />
    </Box>
  );
}

function shortProfileStatus(status: string): string {
  if (status === 'Active') return 'Activo';
  if (status === 'PendingCertificate') return 'Pendiente';
  return CUIT_PROFILE_STATUS_LABEL[status as keyof typeof CUIT_PROFILE_STATUS_LABEL] ?? status;
}
